/**
 * Advanced targeting on the wizard's audience step — interests, keywords and follower
 * look-alikes (PR 4 of audits/2026-10-23/pr45-spec.md). The screen's half of
 * `apps/worker/src/platform/x-targeting-primary.ts`; the words are in primary-targeting-copy.ts.
 *
 * 🔴 These three WIDEN an audience. X unions its «Primary» types and ANDs the rest
 * (campaign-management.md:586–588): a country makes the audience smaller, an interest
 * beside a keyword makes it larger. Nothing here may read as narrowing.
 *
 * The checks copy the worker's (`primaryValueProblem`) so a draft the screen lets through is
 * one the worker accepts; `primary-targeting.test.ts` reads the worker's file to hold the
 * limits equal.
 */

/** The three keyword match types offered — not UNORDERED_KEYWORD, which the guide marks deprecated (G:555). */
export const KEYWORD_TYPES = ["BROAD_KEYWORD", "PHRASE_KEYWORD", "EXACT_KEYWORD"] as const;
export type KeywordType = (typeof KEYWORD_TYPES)[number];

/** The guide's limits per line item — copied from the worker's `PRIMARY_LIMITS`. */
export const PRIMARY_LIMITS = { interests: 100, keywords: 1000, lookalikes: 100 } as const;

export type PrimaryProblem = "keyword-blank" | "keyword-padded" | "keyword-twice" | "lookalike-id";

/**
 * Why a typed keyword cannot be added, or `null`. As the worker: blank and padded are refused
 * (padded rather than trimmed, so what X saves is what was typed), and the same text with the
 * same match type twice is one criterion, not two.
 */
export function keywordProblem(
	text: string,
	type: KeywordType,
	chosen: readonly { type: KeywordType; text: string }[],
): PrimaryProblem | null {
	if (text.trim() === "") return "keyword-blank";
	if (text.trim() !== text) return "keyword-padded";
	if (chosen.some((k) => k.type === type && k.text === text)) return "keyword-twice";
	return null;
}

/** Why a typed look-alike cannot be added, or `null`: an X user id is digits only. */
export function lookalikeProblem(text: string): PrimaryProblem | null {
	return /^[0-9]+$/.test(text) ? null : "lookalike-id";
}
